import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, BRAND } from "@/lib/api";
import LetterheadToggle, { useLetterheadMode } from "@/components/LetterheadToggle";

/**
 * Unified pharmacy-report PDF page.
 * Route: /print/pharmacy-report/:type?from=&to=&payment_mode=&status=&days=&q=&low_only=&autoprint=1
 * Uses the browser's "Print → Save as PDF" dialog (consistent with the rest of the app).
 */
export default function PrintPharmacyReport() {
  const { type } = useParams();
  const [params] = useSearchParams();
  const [hospital, setHospital] = useState(null);
  const [branch, setBranch] = useState(null);
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const letterhead = useLetterheadMode();

  const cfg = REPORTS[type] || REPORTS.sales;

  useEffect(() => {
    (async () => {
      try {
        const meResp = await api.get("/auth/me");
        setHospital(meResp.data.hospital); setBranch(meResp.data.branch); setMe(meResp.data);
        const qs = new URLSearchParams();
        for (const k of cfg.queryKeys) {
          const v = params.get(k);
          if (v !== null && v !== "") qs.set(k, v);
        }
        const url = `/pharmacy/reports/${cfg.path}${qs.toString() ? `?${qs}` : ""}`;
        const { data } = await api.get(url);
        setData(data);
        if (params.get("autoprint") !== "0") setTimeout(() => window.print(), 700);
      } catch (e) {
        setErr(e?.response?.data?.detail || e.message || "Failed to load report");
      }
    })();
    // eslint-disable-next-line
  }, [type, params.toString()]);

  if (err) return <div className="p-10 text-sm text-rose-600">Error: {String(err)}</div>;
  if (!data) return <div className="p-10 text-sm text-ink-muted">Loading…</div>;

  const filters = cfg.filterChips(params);
  const fromTo = (params.get("from") && params.get("to")) ? `${params.get("from")} to ${params.get("to")}` : (params.get("days") ? `Next ${params.get("days")} days` : "All dates");
  const generatedAt = new Date().toLocaleString("en-IN", { hour12: false });

  return (
    <div className="report-a4 mx-auto bg-white p-8 text-sm text-ink" data-testid={`print-pharm-report-${type}`}>
      <style>{`
        @page { size: A4 landscape; margin: 8mm; }
        .report-a4 { width: 297mm; max-width: 297mm; }
        @media print { html, body { background: white; margin: 0 !important; } .report-a4 { box-shadow: none !important; } .no-print { display: none !important; } }
        .report-a4 table { width: 100%; border-collapse: collapse; }
        .report-a4 th, .report-a4 td { padding: 4px 6px; border-bottom: 1px solid #e5e7eb; }
        .report-a4 thead { background: #f1f5f9; }
        .report-a4 thead th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #475569; text-align: left; }
        .report-a4 tbody td { font-size: 11px; }
        .kpi-card { border: 1px solid #e5e7eb; border-radius: 6px; padding: 6px 10px; }
        .kpi-card .lbl { font-size: 9px; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b; }
        .kpi-card .val { font-family: 'Fraunces', serif; font-size: 14px; font-weight: 600; color: #0f172a; }
      `}</style>

      {/* Hospital header (hidden when printing on hospital letterhead) */}
      <LetterheadToggle />
      {!letterhead && (
      <div className="flex items-start justify-between border-b-2 border-brand pb-3">
        <div className="flex items-center gap-3">
          {/* Hospital logo (uploaded) */}
          {hospital?.logo_url ? (
            <img src={hospital.logo_url} alt="" className="h-14 w-14 rounded-md object-contain" />
          ) : (
            <img src={BRAND.icon} alt="" className="h-12 w-12" />
          )}
          {/* Branch logo (uploaded) — shown only if different + present */}
          {branch?.logo_url && branch.logo_url !== hospital?.logo_url && (
            <img src={branch.logo_url} alt="" className="h-14 w-14 rounded-md object-contain border-l border-border pl-3" />
          )}
          <div>
            <div className="font-heading text-2xl font-semibold text-brand">{hospital?.name || BRAND.name}</div>
            <div className="text-[10px] uppercase tracking-wider text-ink-muted">{BRAND.tagline}</div>
            {branch?.name && <div className="text-[11px] text-ink-muted">{branch.name}{branch.code ? ` · ${branch.code}` : ""}{branch.address ? ` · ${branch.address}` : ""}</div>}
            {hospital?.gstin && <div className="text-[11px] text-ink-muted">GSTIN: <span className="font-mono">{hospital.gstin}</span></div>}
            {hospital?.contact && <div className="text-[11px] text-ink-muted">{hospital.contact}</div>}
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading text-lg font-semibold">{cfg.title}</div>
          <div className="text-[11px] text-ink-muted">Period: <b>{fromTo}</b></div>
          <div className="text-[11px] text-ink-muted">Generated: {generatedAt}</div>
          {me?.user?.email && <div className="text-[11px] text-ink-muted">By: {me.user.email}</div>}
        </div>
      </div>
      )}

      {/* Filters chip row */}
      {filters.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2 text-[10px]">
          {filters.map((f, i) => (
            <span key={i} className="rounded-full border border-border bg-secondary/60 px-2 py-0.5">
              <span className="text-ink-muted">{f.label}:</span> <b>{f.value}</b>
            </span>
          ))}
        </div>
      )}

      {/* Body */}
      <div className="mt-4">
        {cfg.render({ data })}
      </div>

      {/* Footer */}
      <div className="mt-6 flex items-center justify-between border-t border-border pt-2 text-[10px] text-ink-muted">
        <div>{hospital?.name || BRAND.name} · Pharmacy department</div>
        <div>{BRAND.footer}</div>
        <div className="page-num" />
      </div>

      <div className="no-print fixed bottom-4 right-4">
        <button onClick={() => window.print()} className="rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white shadow-md">Print / Save as PDF</button>
      </div>
    </div>
  );
}

// ============================================================
// Per-report renderers
// ============================================================
const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dateOnly = (s) => (s || "").slice(0, 10);

const KpiStrip = ({ items }) => (
  <div className="mb-3 grid gap-2" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
    {items.map((k, i) => (
      <div key={i} className="kpi-card">
        <div className="lbl">{k.label}</div>
        <div className="val">{k.value}</div>
      </div>
    ))}
  </div>
);

const REPORTS = {
  sales: {
    title: "Pharmacy Sales Report",
    path: "sales",
    queryKeys: ["from", "to", "payment_mode", "status", "sale_type"],
    filterChips: (p) => ([
      p.get("payment_mode") && { label: "Mode", value: p.get("payment_mode") },
      p.get("status") && { label: "Status", value: p.get("status") },
      p.get("sale_type") && { label: "Type", value: p.get("sale_type") },
    ].filter(Boolean)),
    render: ({ data }) => {
      const s = data.summary || {};
      return (
        <>
          <KpiStrip items={[
            { label: "Invoices", value: s.count ?? 0 },
            { label: "Total", value: inr(s.total) },
            { label: "Paid", value: inr(s.paid) },
            { label: "Outstanding", value: inr(s.outstanding) },
            { label: "GST", value: inr(s.gst) },
          ]} />
          <table>
            <thead><tr><th>Invoice</th><th>Date</th><th>Type</th><th>Customer / Patient</th><th>Mode</th><th style={{textAlign:"right"}}>Total</th><th style={{textAlign:"right"}}>Paid</th><th style={{textAlign:"right"}}>Balance</th><th>Status</th></tr></thead>
            <tbody>
              {(data.items || []).map(r => (
                <tr key={r.id}>
                  <td className="font-mono">{r.invoice_no}</td>
                  <td>{dateOnly(r.created_at)}</td>
                  <td className="capitalize">{r.sale_type}</td>
                  <td>{r.patient_name || "—"}{r.patient_uhid ? ` (${r.patient_uhid})` : ""}</td>
                  <td className="capitalize">{r.payment_mode}</td>
                  <td style={{textAlign:"right"}}>{inr(r.total)}</td>
                  <td style={{textAlign:"right"}}>{inr(r.paid_amount)}</td>
                  <td style={{textAlign:"right"}}>{inr(r.balance)}</td>
                  <td className="capitalize">{r.status}</td>
                </tr>
              ))}
              {(data.items || []).length === 0 && <tr><td colSpan={9} style={{textAlign:"center", padding:"24px", color:"#94a3b8"}}>No sales in this date range.</td></tr>}
            </tbody>
          </table>
        </>
      );
    },
  },
  purchases: {
    title: "Pharmacy Purchases Report",
    path: "purchases",
    queryKeys: ["from", "to", "payment_mode", "status", "supplier_id", "doc_type"],
    filterChips: (p) => ([
      p.get("payment_mode") && { label: "Mode", value: p.get("payment_mode") },
      p.get("status") && { label: "Status", value: p.get("status") },
      p.get("doc_type") && { label: "Doc", value: p.get("doc_type") },
    ].filter(Boolean)),
    render: ({ data }) => {
      const s = data.summary || {};
      return (
        <>
          <KpiStrip items={[
            { label: "Bills", value: s.count ?? 0 },
            { label: "Total", value: inr(s.total) },
            { label: "Paid", value: inr(s.paid) },
            { label: "Outstanding", value: inr(s.outstanding) },
            { label: "GST", value: inr(s.gst) },
          ]} />
          <table>
            <thead><tr><th>Date</th><th>Supplier</th><th>GSTIN</th><th>Doc</th><th>Ref no.</th><th>Mode</th><th style={{textAlign:"right"}}>Total</th><th style={{textAlign:"right"}}>Paid</th><th style={{textAlign:"right"}}>Balance</th><th>Status</th></tr></thead>
            <tbody>
              {(data.items || []).map(r => (
                <tr key={r.id}>
                  <td>{dateOnly(r.created_at)}</td>
                  <td>{r.supplier}</td>
                  <td className="font-mono" style={{fontSize:"9px"}}>{r.supplier_gstin || "—"}</td>
                  <td className="capitalize">{r.doc_type}</td>
                  <td className="font-mono">{r.invoice_no || "—"}</td>
                  <td className="capitalize">{(r.payment_mode || "").replace("_", " ")}</td>
                  <td style={{textAlign:"right"}}>{inr(r.total)}</td>
                  <td style={{textAlign:"right"}}>{inr(r.paid_amount)}</td>
                  <td style={{textAlign:"right"}}>{inr(r.balance)}</td>
                  <td className="capitalize">{r.status || (r.payment_mode === "credit" ? "due" : "paid")}</td>
                </tr>
              ))}
              {(data.items || []).length === 0 && <tr><td colSpan={10} style={{textAlign:"center", padding:"24px", color:"#94a3b8"}}>No purchases in this date range.</td></tr>}
            </tbody>
          </table>
        </>
      );
    },
  },
  stock: {
    title: "Pharmacy Stock Report",
    path: "stock",
    queryKeys: ["q", "low_only", "threshold"],
    filterChips: (p) => ([
      p.get("q") && { label: "Search", value: p.get("q") },
      p.get("low_only") === "true" && { label: "Filter", value: "Low stock only" },
    ].filter(Boolean)),
    render: ({ data }) => {
      const s = data.summary || {};
      return (
        <>
          <KpiStrip items={[
            { label: "SKUs", value: s.count ?? 0 },
            { label: "Total units", value: Number(s.total_units || 0).toLocaleString("en-IN") },
            { label: "Stock value (MRP)", value: inr(s.stock_value_mrp) },
            { label: "Low-stock SKUs", value: s.low_stock_count ?? 0 },
          ]} />
          <table>
            <thead><tr><th>#</th><th>Medicine</th><th>Generic</th><th>Form</th><th style={{textAlign:"right"}}>MRP</th><th style={{textAlign:"right"}}>Stock</th><th style={{textAlign:"right"}}>GST %</th></tr></thead>
            <tbody>
              {(data.items || []).map((r, i) => (
                <tr key={r.id}>
                  <td>{i + 1}</td>
                  <td>{r.name}</td>
                  <td>{r.generic || "—"}</td>
                  <td>{r.form || "—"}</td>
                  <td style={{textAlign:"right"}}>{inr(r.mrp)}</td>
                  <td style={{textAlign:"right"}}>{r.stock || 0}</td>
                  <td style={{textAlign:"right"}}>{r.gst_percent || 0}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      );
    },
  },
  suppliers: {
    title: "Pharmacy Suppliers — Payables Report",
    path: "suppliers",
    queryKeys: ["from", "to"],
    filterChips: () => [],
    render: ({ data }) => {
      const s = data.summary || {};
      return (
        <>
          <KpiStrip items={[
            { label: "Suppliers", value: s.count ?? 0 },
            { label: "Total purchases", value: inr(s.total_purchases) },
            { label: "Total paid", value: inr(s.total_paid) },
            { label: "Total payable", value: inr(s.total_payable) },
          ]} />
          <table>
            <thead><tr><th>Supplier</th><th>GSTIN</th><th>Drug Lic.</th><th>Terms</th><th style={{textAlign:"right"}}>Purchases</th><th style={{textAlign:"right"}}>Total</th><th style={{textAlign:"right"}}>Paid</th><th style={{textAlign:"right"}}>Outstanding</th><th style={{textAlign:"right"}}>Opening</th><th style={{textAlign:"right"}}>Net payable</th></tr></thead>
            <tbody>
              {(data.items || []).map(r => (
                <tr key={r.id}>
                  <td><div className="font-medium">{r.name}</div>{r.contact_person && <div style={{fontSize:"9px",color:"#64748b"}}>{r.contact_person}{r.phone ? ` · ${r.phone}` : ""}</div>}</td>
                  <td className="font-mono" style={{fontSize:"9px"}}>{r.gstin || "—"}</td>
                  <td className="font-mono" style={{fontSize:"9px"}}>{r.drug_license_no || "—"}</td>
                  <td>{r.payment_terms || "—"}</td>
                  <td style={{textAlign:"right"}}>{r.purchases}</td>
                  <td style={{textAlign:"right"}}>{inr(r.total)}</td>
                  <td style={{textAlign:"right"}}>{inr(r.paid)}</td>
                  <td style={{textAlign:"right"}}>{inr(r.outstanding)}</td>
                  <td style={{textAlign:"right"}}>{inr(r.opening_balance)}</td>
                  <td style={{textAlign:"right", fontWeight:600}}>{inr(r.net_payable)}</td>
                </tr>
              ))}
              {(data.items || []).length === 0 && <tr><td colSpan={10} style={{textAlign:"center", padding:"24px", color:"#94a3b8"}}>No suppliers.</td></tr>}
            </tbody>
          </table>
        </>
      );
    },
  },
  expiry: {
    title: "Pharmacy Near-Expiry Report",
    path: "near-expiry",
    queryKeys: ["days"],
    filterChips: (p) => ([p.get("days") && { label: "Days", value: p.get("days") }].filter(Boolean)),
    render: ({ data }) => {
      const s = data.summary || {};
      const todayStr = new Date().toISOString().slice(0, 10);
      return (
        <>
          <KpiStrip items={[
            { label: "Batches at risk", value: s.count ?? 0 },
            { label: "Units at risk", value: Number(s.units_at_risk || 0).toLocaleString("en-IN") },
            { label: "Already expired", value: s.expired_count ?? 0 },
            { label: "Cutoff", value: data.cutoff || "—" },
          ]} />
          <table>
            <thead><tr><th>Medicine</th><th>Batch</th><th>Expiry</th><th style={{textAlign:"right"}}>Qty</th><th style={{textAlign:"right"}}>MRP</th><th>Status</th></tr></thead>
            <tbody>
              {(data.items || []).map(b => {
                const expired = b.expiry && b.expiry < todayStr;
                return (
                  <tr key={b.id}>
                    <td>{b.medicine_name}</td>
                    <td className="font-mono" style={{fontSize:"10px"}}>{b.batch_no}</td>
                    <td>{b.expiry}</td>
                    <td style={{textAlign:"right"}}>{b.qty_remaining}</td>
                    <td style={{textAlign:"right"}}>{inr(b.mrp)}</td>
                    <td style={{color: expired ? "#b91c1c" : "#b45309"}}>{expired ? "EXPIRED" : "Expiring soon"}</td>
                  </tr>
                );
              })}
              {(data.items || []).length === 0 && <tr><td colSpan={6} style={{textAlign:"center", padding:"24px", color:"#94a3b8"}}>No batches expiring in this window.</td></tr>}
            </tbody>
          </table>
        </>
      );
    },
  },
  gst: {
    title: "Pharmacy GST Report",
    path: "gst",
    queryKeys: ["from", "to"],
    filterChips: () => [],
    render: ({ data }) => {
      const o = data.output_tax || {}, ip = data.input_tax || {}, net = data.net_liability || {};
      const Row = ({ section, d }) => (
        <tr><td><b>{section}</b></td><td style={{textAlign:"right"}}>{d.count ?? "—"}</td><td style={{textAlign:"right"}}>{inr(d.taxable)}</td><td style={{textAlign:"right"}}>{inr(d.cgst)}</td><td style={{textAlign:"right"}}>{inr(d.sgst)}</td><td style={{textAlign:"right"}}>{inr(d.gst_total ?? d.total)}</td><td style={{textAlign:"right"}}>{d.grand_total !== undefined ? inr(d.grand_total) : "—"}</td></tr>
      );
      return (
        <>
          <div className="mb-4">
            <table>
              <thead><tr><th>Section</th><th style={{textAlign:"right"}}>Count</th><th style={{textAlign:"right"}}>Taxable</th><th style={{textAlign:"right"}}>CGST</th><th style={{textAlign:"right"}}>SGST</th><th style={{textAlign:"right"}}>GST total</th><th style={{textAlign:"right"}}>Grand total</th></tr></thead>
              <tbody>
                <Row section="Output GST (Sales)" d={o} />
                <Row section="Input GST (Purchases · invoice only)" d={ip} />
                <Row section="Net liability (Output − Input)" d={net} />
              </tbody>
            </table>
          </div>
          <div className="mt-4 mb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Slab-wise breakdown (Sales)</div>
          <table>
            <thead><tr><th>Slab</th><th style={{textAlign:"right"}}>Taxable</th><th style={{textAlign:"right"}}>CGST</th><th style={{textAlign:"right"}}>SGST</th><th style={{textAlign:"right"}}>Total GST</th></tr></thead>
            <tbody>
              {(data.by_slab_sales || []).map(r => (
                <tr key={r.slab}>
                  <td>{r.slab}</td>
                  <td style={{textAlign:"right"}}>{inr(r.taxable)}</td>
                  <td style={{textAlign:"right"}}>{inr(r.cgst)}</td>
                  <td style={{textAlign:"right"}}>{inr(r.sgst)}</td>
                  <td style={{textAlign:"right"}}>{inr(Number(r.cgst) + Number(r.sgst))}</td>
                </tr>
              ))}
              {(data.by_slab_sales || []).length === 0 && <tr><td colSpan={5} style={{textAlign:"center", padding:"16px", color:"#94a3b8"}}>No sales in this date range.</td></tr>}
            </tbody>
          </table>
        </>
      );
    },
  },
};
